import { sanitizeOfficialSources } from "./source-policy";
import type { WidgetInactivityState, WidgetMessage } from "./types";

export const CONVERSATION_STORAGE_KEY = "lpu-de-chat:v1";
export const CONVERSATION_RETENTION_MS = 7 * 24 * 60 * 60 * 1000;
export const MAX_PERSISTED_MESSAGES = 20;

interface StoredConversation {
  version: 1;
  savedAt: number;
  sessionId: string;
  messages: WidgetMessage[];
  inactivity?: WidgetInactivityState;
}

export interface RestoredConversation {
  sessionId: string;
  messages: WidgetMessage[];
  inactivity?: WidgetInactivityState;
}

/**
 * Loads a bounded, unexpired conversation from browser storage.
 */
export function loadConversation(
  storage: Storage | null,
  now = Date.now(),
): RestoredConversation | null {
  if (!storage) {
    return null;
  }
  try {
    const raw = storage.getItem(CONVERSATION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const value = JSON.parse(raw) as Partial<StoredConversation>;
    if (
      value.version !== 1 ||
      typeof value.savedAt !== "number" ||
      now - value.savedAt > CONVERSATION_RETENTION_MS ||
      typeof value.sessionId !== "string" ||
      !Array.isArray(value.messages)
    ) {
      storage.removeItem(CONVERSATION_STORAGE_KEY);
      return null;
    }
    const messages = value.messages
      .map(sanitizeStoredMessage)
      .filter((message): message is WidgetMessage => Boolean(message))
      .slice(-MAX_PERSISTED_MESSAGES);
    const inactivity = sanitizeInactivity(value.inactivity);
    return messages.length > 0
      ? {
          sessionId: value.sessionId.slice(0, 200),
          messages,
          ...(inactivity ? { inactivity } : {}),
        }
      : null;
  } catch {
    return null;
  }
}

/**
 * Persists only bounded display-safe message fields for seven days.
 */
export function saveConversation(
  storage: Storage | null,
  sessionId: string,
  messages: WidgetMessage[],
  now = Date.now(),
  inactivity?: WidgetInactivityState,
): void {
  if (!storage) {
    return;
  }
  const safeMessages = messages
    .filter(
      (message) =>
        message.content.trim().length > 0 && message.status !== "interrupted",
    )
    .slice(-MAX_PERSISTED_MESSAGES)
    .map((message) => ({
      id: message.id.slice(0, 200),
      role: message.role,
      content: sanitizeMessageContent(message.role, message.content),
      ...(message.status === "complete" ? { status: "complete" as const } : {}),
      ...(message.sources
        ? { sources: sanitizeOfficialSources(message.sources) }
        : {}),
      ...(message.suggestions
        ? { suggestions: sanitizeSuggestions(message.suggestions) }
        : {}),
    }));
  const safeInactivity = sanitizeInactivity(inactivity);
  try {
    storage.setItem(
      CONVERSATION_STORAGE_KEY,
      JSON.stringify({
        version: 1,
        savedAt: now,
        sessionId: sessionId.slice(0, 200),
        messages: safeMessages,
        ...(safeInactivity ? { inactivity: safeInactivity } : {}),
      } satisfies StoredConversation),
    );
  } catch {
    // Storage is optional: blocked or full localStorage must not break chat.
  }
}

/**
 * Removes the locally retained conversation without affecting backend caches.
 */
export function clearConversation(storage: Storage | null): void {
  try {
    storage?.removeItem(CONVERSATION_STORAGE_KEY);
  } catch {
    // A privacy action remains best-effort when the browser blocks storage.
  }
}

/**
 * Validates and clamps one untrusted localStorage message.
 */
function sanitizeStoredMessage(value: unknown): WidgetMessage | null {
  const message = value as Partial<WidgetMessage> | null;
  if (
    !message ||
    typeof message.id !== "string" ||
    (message.role !== "user" && message.role !== "bot") ||
    typeof message.content !== "string" ||
    !message.content.trim()
  ) {
    return null;
  }
  return {
    id: message.id.slice(0, 200),
    role: message.role,
    content: sanitizeMessageContent(message.role, message.content),
    ...(message.status === "complete" ? { status: "complete" as const } : {}),
    ...(message.origin === "inactivity"
      ? { origin: "inactivity" as const }
      : {}),
    ...(Array.isArray(message.sources)
      ? { sources: sanitizeOfficialSources(message.sources) }
      : {}),
    ...(Array.isArray(message.suggestions)
      ? { suggestions: sanitizeSuggestions(message.suggestions) }
      : {}),
  };
}

/** Validates locally stored quick questions before rendering them as buttons. */
function sanitizeSuggestions(value: readonly unknown[]): string[] {
  return [
    ...new Set(
      value
        .filter((item): item is string => typeof item === "string")
        .map((item) => item.replace(/\s+/g, " ").trim().slice(0, 100))
        .filter(Boolean),
    ),
  ].slice(0, 2);
}

/**
 * Bounds untrusted user input while preserving the backend's complete answer.
 */
function sanitizeMessageContent(
  role: WidgetMessage["role"],
  content: string,
): string {
  return role === "user" ? content.slice(0, 300) : content;
}

/** Retains only a valid bounded inactivity deadline and prepared message. */
function sanitizeInactivity(
  value: WidgetInactivityState | undefined,
): WidgetInactivityState | undefined {
  if (!value || !Number.isFinite(value.consecutiveCount)) return undefined;
  const consecutiveCount = Math.max(
    0,
    Math.min(Math.floor(value.consecutiveCount), 5),
  );
  if (
    typeof value.prepareAt !== "number" ||
    !Number.isFinite(value.prepareAt) ||
    typeof value.sendAt !== "number" ||
    !Number.isFinite(value.sendAt) ||
    value.sendAt < value.prepareAt
  ) {
    return { consecutiveCount };
  }
  const preparedMessage = value.preparedMessage
    ?.replace(/\s+/g, " ")
    .trim()
    .slice(0, 150);
  return {
    consecutiveCount,
    prepareAt: value.prepareAt,
    sendAt: value.sendAt,
    ...(preparedMessage ? { preparedMessage } : {}),
  };
}
